import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Button,
  Select,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { v4 as uuidv4 } from "uuid";

type ControlType = "text" | "checkbox" | "dropdown";

interface FormControlConfig {
  id: string;
  type: ControlType;
  label: string;
  name: string;
  sm: number;
  options?: string[];
}

interface CardSection {
  id: string;
  title: string;
  controls: FormControlConfig[];
}

// Draggable item from palette
const DraggableItem = ({ control }: { control: FormControlConfig }) => {
  const [, drag] = useDrag(() => ({
    type: "FORM_CONTROL",
    item: control,
  }));

  return (
    <Box ref={drag} sx={{ p: 1, mb: 1, border: "1px solid #ccc", cursor: "move" }}>
      {control.label}
    </Box>
  );
};

// Droppable card section
const DroppableCard = ({
  card,
  onDropControl,
  onRemoveCard,
  onRemoveControl,
  onUpdateGridSize,
  onUpdateTitle,
}: {
  card: CardSection;
  onDropControl: (cardId: string, item: FormControlConfig) => void;
  onRemoveCard: (cardId: string) => void;
  onRemoveControl: (cardId: string, ctrlId: string) => void;
  onUpdateGridSize: (cardId: string, ctrlId: string, sm: number) => void;
  onUpdateTitle: (cardId: string, newTitle: string) => void;
}) => {
  const [, drop] = useDrop(() => ({
    accept: "FORM_CONTROL",
    drop: (item: FormControlConfig) => {
      onDropControl(card.id, { ...item, id: uuidv4(), sm: 4 });
    },
  }));

  return (
    <Box ref={drop} sx={{ border: "2px solid #aaa", borderRadius: 2, p: 2, mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <TextField
          variant="standard"
          value={card.title}
          onChange={(e) => onUpdateTitle(card.id, e.target.value)}
          label="Card Title"
        />
        <IconButton onClick={() => onRemoveCard(card.id)}>
          <DeleteIcon />
        </IconButton>
      </Box>
      <Grid container spacing={2}>
        {card.controls.map((ctrl) => (
          <Grid key={ctrl.id} item sm={ctrl.sm}>
            <Box sx={{ border: "1px solid #ddd", p: 2, position: "relative" }}>
              <Typography>{ctrl.label}</Typography>
              {ctrl.type === "text" && <TextField fullWidth size="small" />}
              {ctrl.type === "checkbox" && (
                <label>
                  <input type="checkbox" /> {ctrl.name}
                </label>
              )}
              {ctrl.type === "dropdown" && (
                <Select fullWidth size="small" defaultValue="">
                  {ctrl.options?.map((opt, idx) => (
                    <MenuItem key={idx} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              )}
              <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  label="Grid (sm)"
                  type="number"
                  size="small"
                  value={ctrl.sm}
                  onChange={(e) =>
                    onUpdateGridSize(card.id, ctrl.id, parseInt(e.target.value))
                  }
                  inputProps={{ min: 1, max: 12 }}
                  sx={{ width: 80 }}
                />
                <IconButton onClick={() => onRemoveControl(card.id, ctrl.id)} size="small">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const FormBuilder = () => {
  const [cards, setCards] = useState<CardSection[]>([]);
  const [jsonOutput, setJsonOutput] = useState("");

  const controlsPalette: FormControlConfig[] = [
    { id: "text", type: "text", label: "Text Input", name: "text", sm: 4 },
    { id: "checkbox", type: "checkbox", label: "Checkbox", name: "checkbox", sm: 4 },
    {
      id: "dropdown",
      type: "dropdown",
      label: "Dropdown",
      name: "dropdown",
      sm: 4,
      options: ["Option 1", "Option 2"],
    },
  ];

  const addNewCard = () => {
    setCards((prev) => [
      ...prev,
      { id: uuidv4(), title: "New Section", controls: [] },
    ]);
  };

  const handleDropControl = (cardId: string, control: FormControlConfig) => {
    setCards((prev) =>
      prev.map((card) =>
        card.id === cardId
          ? { ...card, controls: [...card.controls, control] }
          : card
      )
    );
  };

  const handleRemoveCard = (cardId: string) => {
    setCards((prev) => prev.filter((card) => card.id !== cardId));
  };

  const handleRemoveControl = (cardId: string, ctrlId: string) => {
    setCards((prev) =>
      prev.map((card) =>
        card.id === cardId
          ? {
              ...card,
              controls: card.controls.filter((ctrl) => ctrl.id !== ctrlId),
            }
          : card
      )
    );
  };

  const handleUpdateGridSize = (cardId: string, ctrlId: string, sm: number) => {
    setCards((prev) =>
      prev.map((card) =>
        card.id === cardId
          ? {
              ...card,
              controls: card.controls.map((ctrl) =>
                ctrl.id === ctrlId ? { ...ctrl, sm } : ctrl
              ),
            }
          : card
      )
    );
  };

  const handleUpdateTitle = (cardId: string, newTitle: string) => {
    setCards((prev) =>
      prev.map((card) => (card.id === cardId ? { ...card, title: newTitle } : card))
    );
  };

  const saveJSON = () => {
    setJsonOutput(JSON.stringify(cards, null, 2));
  };

  const loadJSON = () => {
    try {
      const parsed = JSON.parse(jsonOutput) as CardSection[];
      setCards(parsed);
    } catch (err) {
      alert("Invalid JSON");
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ display: "flex", gap: 4 }}>
        <Box>
          <Typography variant="h6">Palette</Typography>
          {controlsPalette.map((ctrl) => (
            <DraggableItem key={ctrl.id} control={ctrl} />
          ))}
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Canvas
          </Typography>
          <Button variant="outlined" onClick={addNewCard} sx={{ mb: 2 }}>
            + Add Card
          </Button>
          {cards.map((card) => (
            <DroppableCard
              key={card.id}
              card={card}
              onDropControl={handleDropControl}
              onRemoveCard={handleRemoveCard}
              onRemoveControl={handleRemoveControl}
              onUpdateGridSize={handleUpdateGridSize}
              onUpdateTitle={handleUpdateTitle}
            />
          ))}
        </Box>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button onClick={saveJSON} variant="contained" sx={{ mr: 2 }}>
          Save JSON
        </Button>
        <Button onClick={loadJSON} variant="outlined">
          Load JSON
        </Button>
        <TextField
          multiline
          minRows={6}
          fullWidth
          margin="normal"
          label="Form JSON"
          value={jsonOutput}
          onChange={(e) => setJsonOutput(e.target.value)}
        />
      </Box>
    </DndProvider>
  );
};

export default FormBuilder;
